import React, { useEffect, useState, useRef, useCallback } from "react";
import { DownOutlined } from "@ant-design/icons";
import * as com from "../../Common";
import { Button, Dropdown, Select, Tag } from "antd";

import "./common.css";
import { useSelector } from "react-redux";

interface CurrentStatusProps {
  currentStatus: string;
  currentStage: string;
  loanID: string;
  handleStatusChange: (loanID: string, status: string) => void;
}

const CurrentStatus = ({
  currentStatus,
  currentStage:stage,
  loanID,
  handleStatusChange,
}: CurrentStatusProps) => {
   const appStage = useSelector(
    (state: any) => state.application.currentStage
  );

  const currentStage = stage ?? appStage

  const loanPurpose: string = useSelector(
    (state: any) => state.application?.property?.purpose
  );

  const [statusToDisplay, setStatusToDisplay] = useState(currentStatus);
  const lastProcessedStage = useRef(currentStage);
  const lastProcessedStatus = useRef(currentStatus);
  const isUpdating = useRef(false);
  const isUserAction = useRef(false);

  // 当 currentStage 或 currentStatus 变化时，检查是否需要更新 status
  useEffect(() => {
    // 如果是用户手动操作触发的，不处理
    if (isUserAction.current) {
      isUserAction.current = false;
      return;
    }

    // 防止重复处理相同的 stage 和 status
    if (lastProcessedStage.current === currentStage && lastProcessedStatus.current === currentStatus) {
      return;
    }

    // 防止正在更新时重复调用
    if (isUpdating.current) {
      return;
    }

    console.log("CurrentStatus useEffect - stage:", currentStage, "status:", currentStatus);
    
    const applicableStatuses = com.CurrentStatusUtils.getStatusesByStage(
      currentStage, 
      loanPurpose
    );
    
    // 检查当前 status 是否在新的 stage 下有效
    const isValidStatus = applicableStatuses.some(
      (status) => status.name === currentStatus
    );
    
    if (!isValidStatus && applicableStatuses.length > 0) {
      // 如果当前 status 无效，使用第一个有效的 status
      const firstApplicableStatus = applicableStatuses[0].name;
      
      // 避免重复更新相同的状态
      if (firstApplicableStatus !== currentStatus) {
        isUpdating.current = true;
        setStatusToDisplay(firstApplicableStatus);
        // 使用第一个有效的status作为显示时，应保存显示的status，保持显示和实际状态一致
        handleStatusChange(loanID, firstApplicableStatus);
        // 注意：这里不再自动调用 handleStatusChange，因为父组件的 handleStageChange 已经处理了
        // 但是我们需要确保 UI 显示正确
      }
    } else {
      // 如果当前 status 有效，直接使用
      setStatusToDisplay(currentStatus);
    }
    
    lastProcessedStage.current = currentStage;
    lastProcessedStatus.current = currentStatus;
    isUpdating.current = false;
  }, [currentStage, currentStatus, loanPurpose]);

  // 当 statusToDisplay 变化时，确保下拉选项也更新
  const items = com.CurrentStatusUtils.getStatusesByStage(
    currentStage,
    loanPurpose
  ).map(({ code, name }, idx) => {
    return {
      value: name,
      key: code,
      label: <div>{com.CurrentStatusUtils.getStatusName(name)}</div>,
    };
  });


  useEffect(() => {
    const applicableStatuses =
      com.CurrentStatusUtils.getStatusesByStage(currentStage, loanPurpose);
    if (!applicableStatuses.some((status) => status.name === currentStatus)) {
      const firstApplicableStatus = applicableStatuses[0]?.name;
      setStatusToDisplay(firstApplicableStatus);
      handleStatusChange(loanID, firstApplicableStatus);
    } else {
      setStatusToDisplay(currentStatus);
    }
  }, [currentStatus, currentStage]);

  return (
    <div className="loansStatusContainer">
      <div
        className="loansStatus"
        style={{
          borderColor:
            com.CurrentStatusUtils.getStatusBorderColor(statusToDisplay),
          backgroundColor:
            com.CurrentStatusUtils.getStatusBackgroundColor(statusToDisplay),
        }}
      >
        <Select
          style={{
            backgroundColor:
              com.CurrentStatusUtils.getStatusBackgroundColor(statusToDisplay),
            color: com.CurrentStatusUtils.getStatusTextColor(statusToDisplay),
            border: "none",
            borderRadius: "6px",
          }}
          value={com.CurrentStatusUtils.getStatusName(statusToDisplay)}
          onChange={(value) => {
            console.log("User changed status to:", value);
            isUserAction.current = true;
            setStatusToDisplay(value);
            handleStatusChange(loanID, value);
          }}
          options={items}
          suffixIcon={
            <img src="/images/stepper-menu-arrow-down.svg" alt="down-arrow" />
          }
        />
      </div>
    </div>
  );
};

export default CurrentStatus;
